// SCR-STAFF-202 — فهرست‌های قیمت (US-CAT-020 تا 025): فهرست پایه و فهرست‌های شعبه (فقط ردیف‌های متفاوت؛ بقیه به ارث از پایه —
// AC-CAT-023.1)، جدول خدمت با قیمت جاری و زمان‌بندی‌شده و ویرایش درجا با تاریخ مؤثر (امروز یا آینده — BR-CAT-026)؛ گروه‌های
// ارائه‌دهنده (AC-CAT-022.1) و پروفایل‌های مالیاتی (US-CAT-004) در تب‌های همین صفحه. مبالغ در واحد نمایش مستأجر و ذخیره ریالی.
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type PriceRowView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { errorMessageKey, useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  useDisplaySettings,
  zBusinessDate,
  zMoneyMinor,
  type DataTableColumn,
} from '@phoenix/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { BranchListDialog, ProviderGroupsTab, TaxProfilesTab } from './PriceListsParts';
import { catKeys, servicePath, ServiceStatusChip, todayIn, usePriceLists } from './common';

export const SCR_STAFF_202 = 'SCR-STAFF-202';

function inlineSchema(today: string) {
  return z.object({ amount: zMoneyMinor(), effectiveFrom: zBusinessDate() }).superRefine((value, ctx) => {
    if (value.effectiveFrom && value.effectiveFrom < today) {
      ctx.addIssue({
        code: 'custom',
        path: ['effectiveFrom'],
        message: 'errors:error.price_effective_date_past',
      });
    }
  });
}
type InlineForm = z.input<ReturnType<typeof inlineSchema>>;

/** ویرایش درجا: مبلغ و تاریخ مؤثر؛ Enter ثبت، Esc انصراف. */
function InlinePriceEditor({
  row,
  today,
  listId,
  onDone,
}: {
  row: PriceRowView;
  today: string;
  listId: string | null;
  onDone: () => void;
}) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [serverError, setServerError] = useState<string>();

  // BR-CAT-035: قیمت بالاتر از تعرفه مصوب (PHX-CAT-0331) با تأیید صریح ثبت می‌شود (AC-CAT-033.2).
  const [aboveTariff, setAboveTariff] = useState<{ approved: number; requested: number } | null>(null);
  const { control, handleSubmit } = useForm<InlineForm>({
    resolver: zodResolver(inlineSchema(today)),
    defaultValues: {
      amount: row.current
        ? Number(row.current.amount.amount)
        : row.inherited
          ? Number(row.inherited.amount.amount)
          : undefined,
      effectiveFrom: today,
    },
  });

  // ویرایش سریع روش «به ازای واحد» بدون پله را حفظ می‌کند؛ پله‌ها و بازه فقط در تب قیمت خدمت ویرایش می‌شوند.
  const source = row.current ?? row.inherited;
  const method: 'PerUnit' | 'Fixed' =
    source?.method === 'PerUnit' && (source.tiers?.length ?? 0) <= 1 ? 'PerUnit' : 'Fixed';

  const save = useMutation({
    mutationFn: (input: InlineForm & { confirmAboveApprovedTariff: boolean }) => {
      const body = {
        serviceId: row.serviceId,
        providerId: null,
        method,
        amount: { amount: Number(input.amount), currency: 'IRR' },
        effectiveFrom: input.effectiveFrom,
        confirmAboveApprovedTariff: input.confirmAboveApprovedTariff,
      };
      return unwrap(
        listId
          ? tenantApi.POST('/api/v1/catalog/price-lists/{id}/entries', {
              params: { path: { id: listId } },
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            })
          : tenantApi.POST('/api/v1/catalog/price-lists/base/entries', {
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
      );
    },
    onMutate: () => setServerError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setAboveTariff(null);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onDone();
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'PHX-CAT-0331') {
        const problem = error as unknown as { approved?: number; requested?: number };
        setAboveTariff({ approved: Number(problem.approved ?? 0), requested: Number(problem.requested ?? 0) });
        return;
      }

      setServerError(isApiError(error) ? errorMessageKey(error.code) : 'errors:error.unexpected');
    },
  });

  const submit = (confirmAboveApprovedTariff = false) =>
    void handleSubmit((form) => save.mutate({ ...form, confirmAboveApprovedTariff }))();

  return (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      spacing={1}
      alignItems="flex-start"
      data-testid={`price-editor-${row.code}`}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        } else if (event.key === 'Escape') {
          onDone();
        }
      }}
    >
      <MoneyFieldRHF control={control} name="amount" labelKey="cat:prices.amount" required autoFocus />
      <JalaliDatePickerRHF
        control={control}
        name="effectiveFrom"
        labelKey="cat:prices.effective_from"
        required
      />
      <Tooltip title={t('common:actions.save')}>
        <span>
          <IconButton
            aria-label={t('common:actions.save')}
            onClick={() => submit()}
            disabled={save.isPending}
            data-testid="price-inline-save"
          >
            <CheckIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('common:actions.cancel')}>
        <IconButton aria-label={t('common:actions.cancel')} onClick={onDone}>
          <CloseIcon />
        </IconButton>
      </Tooltip>
      {serverError && (
        <Typography variant="caption" color="error" role="alert" data-testid="price-inline-error">
          {t(serverError)}
        </Typography>
      )}
      <ConfirmDialog
        open={aboveTariff !== null}
        titleKey="cat:prices.above_tariff_title"
        messageKey="cat:prices.above_tariff_message"
        messageValues={{
          approved: faDigits(aboveTariff?.approved ?? 0),
          requested: faDigits(aboveTariff?.requested ?? 0),
        }}
        confirmKey="cat:prices.above_tariff_confirm"
        busy={save.isPending}
        onConfirm={() => submit(true)}
        onCancel={() => setAboveTariff(null)}
      />
    </Stack>
  );
}

/** تب قیمت‌ها: انتخاب فهرست (پایه یا شعبه)، فهرست شعبه تازه و جدول ردیف‌ها. */
function PriceRowsTab() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('cat.price.manage');
  const { timeZone } = useDisplaySettings();
  const lists = usePriceLists();
  const [listId, setListId] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query.trim());
  const [editing, setEditing] = useState<string | null>(null);

  const baseId = lists.data?.items.find((list) => list.type === 'Base')?.id ?? '';
  const selected = listId && listId !== baseId ? listId : null;
  const filter = { q: deferredQuery || undefined, list: selected ?? 'base' };
  const rows = useQuery({
    queryKey: catKeys.rows(session?.tenantId, filter),
    queryFn: () =>
      unwrap(
        selected
          ? tenantApi.GET('/api/v1/catalog/price-lists/{id}/rows', {
              params: { path: { id: selected }, query: { q: filter.q } },
            })
          : tenantApi.GET('/api/v1/catalog/price-lists/base/rows', { params: { query: { q: filter.q } } }),
      ),
    placeholderData: keepPreviousData,
  });
  const today = rows.data?.today ?? todayIn(timeZone);

  const columns: DataTableColumn<PriceRowView>[] = [
    { id: 'code', headerKey: 'cat:services.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'name',
      headerKey: 'cat:services.columns.name',
      cell: (row) => <RouterLink to={servicePath(row.serviceId)}>{row.name}</RouterLink>,
    },
    {
      id: 'status',
      headerKey: 'cat:services.columns.status',
      cell: (row) => <ServiceStatusChip status={row.status} />,
    },
    {
      id: 'current',
      headerKey: 'cat:prices.columns.current',
      cell: (row) =>
        editing === row.serviceId ? (
          <InlinePriceEditor row={row} today={today} listId={selected} onDone={() => setEditing(null)} />
        ) : row.current ? (
          <span data-testid={`price-current-${row.code}`}>
            <Money amountMinor={row.current.amount.amount} currency={row.current.amount.currency} />
          </span>
        ) : row.inherited ? (
          <Stack spacing={0.25} data-testid={`price-inherited-${row.code}`}>
            <Money amountMinor={row.inherited.amount.amount} currency={row.inherited.amount.currency} />
            <Typography variant="caption" color="text.secondary">
              {t('cat:prices.inherited')}
            </Typography>
          </Stack>
        ) : (
          <Typography component="span" variant="body2" color="text.secondary">
            {t('cat:prices.not_defined')}
          </Typography>
        ),
    },
    {
      id: 'scheduled',
      headerKey: 'cat:prices.columns.scheduled',
      cell: (row) =>
        row.scheduled ? (
          <Stack spacing={0.25} data-testid={`price-scheduled-${row.code}`}>
            <Money amountMinor={row.scheduled.amount.amount} currency={row.scheduled.amount.currency} />
            <Typography variant="caption" color="text.secondary">
              {t('cat:prices.from')} <JalaliDate value={row.scheduled.effectiveFrom} />
            </Typography>
          </Stack>
        ) : (
          t('common:states.none')
        ),
    },
    {
      id: 'overrides',
      headerKey: 'cat:prices.columns.provider_overrides',
      cell: (row) =>
        Number(row.providerOverrideCount) > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={t('cat:prices.provider_overrides', { count: faDigits(row.providerOverrideCount) })}
          />
        ) : (
          t('common:states.none')
        ),
    },
    {
      id: 'actions',
      headerKey: 'cat:prices.columns.actions',
      cell: (row) => (
        <Tooltip title={t('cat:prices.edit_inline')}>
          <span>
            <IconButton
              aria-label={t('cat:prices.edit_named', { name: row.name })}
              disabled={!write.canWrite || editing !== null}
              onClick={() => setEditing(row.serviceId)}
              data-testid={`price-edit-${row.code}`}
            >
              <EditOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <DataTable
        captionKey="cat:prices.caption"
        columns={columns}
        rows={rows.data?.items}
        getRowId={(row) => row.serviceId}
        isLoading={rows.isPending}
        error={rows.error}
        onRetry={() => void rows.refetch()}
        emptyTitleKey="cat:prices.empty"
        toolbar={
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ p: 1.5 }}
            alignItems={{ md: 'center' }}
          >
            <TextField
              select
              size="small"
              label={t('cat:prices.list')}
              value={listId || baseId}
              onChange={(event) => {
                setEditing(null);
                setListId(event.target.value);
              }}
              sx={{ minWidth: 220 }}
              slotProps={{ htmlInput: { 'data-testid': 'price-list-select' } }}
            >
              {(lists.data?.items ?? []).map((list) => (
                <MenuItem key={list.id} value={list.id}>
                  {list.type === 'Base' ? t('cat:price_list.Base') : list.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('cat:services.search')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'prices-search' } }}
              sx={{ minWidth: 240 }}
            />
            <Button
              startIcon={<AddIcon />}
              disabled={!write.canWrite}
              onClick={() => setCreating(true)}
              data-testid="branch-list-create"
            >
              {t('cat:prices.add_branch_list')}
            </Button>
          </Stack>
        }
      />
      {creating && (
        <BranchListDialog
          onClose={(created) => {
            setCreating(false);
            if (created) {
              setListId(created);
            }
          }}
        />
      )}
    </Stack>
  );
}

export function PricesPage() {
  const { t } = useTranslation('cat');
  const access = useAccess();
  const [tab, setTab] = useState('prices');

  return (
    <ListPage
      screenId={SCR_STAFF_202}
      titleKey="cat:prices.title"
      subtitleKey="cat:prices.subtitle"
      badges={
        <Chip
          size="small"
          variant="outlined"
          label={t('cat:price_list.Base')}
          data-testid="price-list-base"
        />
      }
      actions={
        access.can('cat.price.bulk_change') ? (
          <Button
            component={RouterLink}
            to="/settings/prices/bulk"
            variant="outlined"
            data-testid="bulk-link"
          >
            {t('cat:bulk.open')}
          </Button>
        ) : undefined
      }
    >
      <Tabs value={tab} onChange={(_, value: string) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="prices" label={t('cat:prices.tabs.prices')} />
        <Tab value="groups" label={t('cat:prices.tabs.groups')} data-testid="tab-groups" />
        <Tab value="tax" label={t('cat:prices.tabs.tax')} data-testid="tab-tax" />
      </Tabs>
      {tab === 'prices' && <PriceRowsTab />}
      {tab === 'groups' && <ProviderGroupsTab />}
      {tab === 'tax' && <TaxProfilesTab />}
    </ListPage>
  );
}
